if (!customElements.get('collection-filters')) {
	customElements.define(
		'collection-filters',
		class CollectionFilters extends HTMLElement {
			constructor() {
				super();
				this.debouncedOnChange = this.debounce(this.onChange.bind(this), 500);
				this.abortController = null;
			}

			connectedCallback() {
				this.form = this.querySelector('[data-collection-filters-form]');
				this.loadingOverlay = this.querySelector('[data-loading]');

				this.productsContainer = document.querySelector(
					'#resultsContainer .grid__content'
				);

				this.productCountContainer = document.querySelector('#productCount');

				this.filtersContainer =
					document.querySelector('[data-filters-container]') ||
					document.querySelector('.collection-product-grid__sidebar');

				this.activeFiltersContainer =
					document.querySelector('#activeFilters') ||
					document.querySelector('.collection-product-grid__active-filters');

				this.paginationContainer =
					document.querySelector('[data-pagination-container]') ||
					document.querySelector('.pagination');

				this.filtersHeader = document.querySelector(
					'.collection-product-grid__header'
				);

				this.bindEvents();
				this.initPriceRangeSliders();
				this.setupMobileFiltersObserver();
				document.addEventListener(
					'DOMContentLoaded',
					this.initColors.bind(this)
				);

				window.addEventListener('popstate', this.onHistoryChange.bind(this));
			}

			disconnectedCallback() {
				window.removeEventListener('popstate', this.onHistoryChange.bind(this));
			}

			bindEvents() {
				const filterInputs = this.querySelectorAll('[data-filter-input]');
				filterInputs.forEach((input) => {
					input.addEventListener('change', (e) => {
						this.debouncedOnChange();
					});
				});

				const clearAllButton = this.querySelector('[data-clear-all]');
				if (clearAllButton) {
					clearAllButton.addEventListener('click', this.onClearAll.bind(this));
				}

				this.querySelectorAll('[data-remove-filter]').forEach((button) => {
					button.addEventListener('click', this.onRemoveFilter.bind(this));
				});

				this.querySelectorAll('[data-price-min], [data-price-max]').forEach(
					(input) => {
						input.addEventListener('change', this.debouncedOnChange);
					}
				);
			}

			initColors() {
				const searchParams = window.location.search;
				this.fetchAndRender(searchParams, false);
			}

			initPriceRangeSliders() {
				const rangeSliders = this.querySelectorAll('[data-range-slider]');

				rangeSliders.forEach((slider) => {
					const minRange = slider.querySelector('[data-range-min]');
					const maxRange = slider.querySelector('[data-range-max]');
					const progress = slider.querySelector('[data-range-progress]');

					const container = slider.closest('.collection-filters__price-range');
					const minInput = container?.querySelector('[data-price-min]');
					const maxInput = container?.querySelector('[data-price-max]');

					if (!minRange || !maxRange || !progress) return;

					const updateProgress = () => {
						const min = parseInt(minRange.value);
						const max = parseInt(maxRange.value);
						const rangeMax = parseInt(minRange.max);

						const leftPercent = (min / rangeMax) * 100;
						const rightPercent = 100 - (max / rangeMax) * 100;

						progress.style.left = `${leftPercent}%`;
						progress.style.right = `${rightPercent}%`;
					};

					minRange.addEventListener('input', (e) => {
						const min = parseInt(e.target.value);
						const max = parseInt(maxRange.value);

						if (min > max) {
							e.target.value = max;
						}

						if (minInput) {
							minInput.value = e.target.value;
						}

						updateProgress();
					});

					maxRange.addEventListener('input', (e) => {
						const min = parseInt(minRange.value);
						const max = parseInt(e.target.value);

						if (max < min) {
							e.target.value = min;
						}

						if (maxInput) {
							maxInput.value = e.target.value;
						}

						updateProgress();
					});

					minRange.addEventListener('change', this.debouncedOnChange);
					maxRange.addEventListener('change', this.debouncedOnChange);

					if (minInput) {
						minInput.addEventListener('input', (e) => {
							minRange.value = e.target.value || 0;
							updateProgress();
						});
					}

					if (maxInput) {
						maxInput.addEventListener('input', (e) => {
							maxRange.value = e.target.value || maxRange.max;
							updateProgress();
						});
					}
				});
			}

			initMobileFilters() {
				const toggleEl = document.querySelector('[data-entry-filter-toggle]');
				const closeEl = document.querySelector('[data-entry-filter-close]');
				const layoutEl = document.querySelector('[data-entry-filter-layout]');
				if (!toggleEl || !closeEl || !layoutEl) return;

				const label = toggleEl.querySelector(
					'.collection-entry-filter-toggle__label'
				);
				const textClosed =
					toggleEl.getAttribute('data-filter-text-closed') || 'Filters';
				const textOpen =
					toggleEl.getAttribute('data-filter-text-open') || 'Close filters';
				if (toggleEl.getAttribute('data-initialized') !== 'true') {
					toggleEl.addEventListener('click', function () {
						if (!layoutEl) return;
						const isOpen = layoutEl.classList.toggle(
							'collection-product-grid__layout--filters-visible'
						);
						toggleEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
						if (label) {
							label.textContent = isOpen ? textOpen : textClosed;
						}
					});
					toggleEl.setAttribute('data-initialized', 'true');
				}

				if (closeEl.getAttribute('data-initialized') !== 'true') {
					closeEl.addEventListener('click', function () {
						if (!layoutEl) return;
						layoutEl.classList.remove(
							'collection-product-grid__layout--filters-visible'
						);
						toggleEl.setAttribute('aria-expanded', 'false');
						if (label) {
							label.textContent = textClosed;
						}
					});
					closeEl.setAttribute('data-initialized', 'true');
				}
			}

			setupMobileFiltersObserver() {
				const container =
					this.productsContainer ||
					document.querySelector('#resultsContainer .grid__content');

				if (!container) return;
				const observer = new IntersectionObserver(([entry]) => {
					if (
						entry.isIntersecting &&
						window.innerWidth < 1100 &&
						!!this.filtersHeader
					) {
						this.filtersHeader.classList.add('visible');
					} else {
						this.filtersHeader.classList.remove('visible');
					}
				});
				observer.observe(container);
			}

			async onChange(additionalFormData = null) {
				if (!this.form) {
					console.error('[CollectionFilters] No form found!');
					return;
				}
				const formData = new FormData(this.form);
				if (additionalFormData) {
					for (const key of additionalFormData.keys()) {
						formData.delete(key);
					}
					for (const [key, value] of additionalFormData.entries()) {
						formData.append(key, value);
					}
				}

				const searchParams = this.buildSearchParams(formData);
				this.fetchAndRender(searchParams);
			}

			onClearAll(event) {
				event.preventDefault();
				const url = new URL(event.currentTarget.href);
				this.fetchAndRender(url.search);
			}

			onRemoveFilter(event) {
				event.preventDefault();
				const url = new URL(event.currentTarget.href);
				this.fetchAndRender(url.search);
			}

			onHistoryChange(event) {
				const searchParams = window.location.search;
				this.fetchAndRender(searchParams, false);
			}

			buildSearchParams(formData) {
				for (const pair of formData.entries()) {
					console.log(`[CollectionFilters] FormData: ${pair[0]} = ${pair[1]}`);
				}
				const params = new URLSearchParams();

				const checkboxGroups = {};

				for (const [key, value] of formData.entries()) {
					if (value === '' || value === null || value === undefined) continue;

					if (key.includes('filter.v.price')) {
						const priceValue = parseFloat(value);
						if (!isNaN(priceValue)) {
							params.append(key, Math.round(priceValue * 100));
						}
						continue;
					}

					if (!checkboxGroups[key]) {
						checkboxGroups[key] = [];
					}
					checkboxGroups[key].push(value);
				}

				for (const [key, values] of Object.entries(checkboxGroups)) {
					values.forEach((value) => {
						params.append(key, value);
					});
				}

				if (formData.get('sort_by')) {
					params.set('sort_by', formData.get('sort_by'));
				}

				return params.toString() ? `?${params.toString()}` : '';
			}

			async fetchAndRender(searchParams, updateHistory = true) {
				if (this.abortController) {
					this.abortController.abort();
				}

				this.abortController = new AbortController();

				this.showLoading();

				const url = `${window.location.pathname}${searchParams}`;

				const hasColorParam = [
					...new URLSearchParams(searchParams).keys()
				].some((key) => key.toLowerCase().includes('color'));

				const noColorParams = new URLSearchParams(searchParams);
				[...noColorParams.keys()].forEach((key) => {
					if (key.toLowerCase().includes('color')) {
						noColorParams.delete(key);
					}
				});

				try {
					const response = await fetch(url, {
						signal: this.abortController.signal,
						headers: {
							'X-Requested-With': 'XMLHttpRequest'
						}
					});

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const html = await response.text();
					if (hasColorParam) {
						const url =
							noColorParams.size > 0
								? `${window.location.pathname}?${noColorParams.toString()}`
								: window.location.pathname;
						const noColorResponse = await fetch(url, {
							signal: this.abortController.signal,
							headers: {
								'X-Requested-With': 'XMLHttpRequest'
							}
						});
						if (!noColorResponse.ok) {
							throw new Error(`HTTP error! status: ${noColorResponse.status}`);
						}
						const noColorHtml = await noColorResponse.text();
						this.renderResults(html, noColorHtml);
					} else {
						this.renderResults(html);
					}

					if (updateHistory) {
						window.history.pushState({ searchParams }, '', url);
					}

					this.dispatchEvent(
						new CustomEvent('collection:filtered', {
							bubbles: true,
							detail: { url, searchParams }
						})
					);
					this.initMobileFilters();
				} catch (error) {
					if (error.name === 'AbortError') {
						return;
					}
					console.error('Error fetching filtered results:', error);
					this.showError();
				} finally {
					this.hideLoading();
					this.abortController = null;
				}
			}

			renderResults(html, noColorHtml = null) {
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, 'text/html');

				if (this.productsContainer) {
					const newProducts = doc.querySelector(
						'#resultsContainer .grid__content'
					);

					if (newProducts) {
						this.productsContainer.innerHTML = newProducts.innerHTML;
						if (noColorHtml) {
							const noColorDoc = parser.parseFromString(
								noColorHtml,
								'text/html'
							);
							const noColorProducts = noColorDoc.querySelector(
								'#resultsContainer .grid__content'
							);
							if (noColorProducts) {
								const noColorIds = [
									...newProducts.querySelectorAll('[data-product-id]')
								].map((el) => el.getAttribute('data-product-id'));
								noColorProducts
									.querySelectorAll('[data-product-id]')
									.forEach((prod) => {
										const prodId = prod.getAttribute('data-product-id');
										if (noColorIds.includes(prodId)) {
											prod.remove();
										}
									});
								this.productsContainer.innerHTML += noColorProducts.innerHTML;
							}
						}
						this.animateProducts();
					}
				}

				if (this.productCountContainer) {
					const newCount =
						doc.querySelector('[data-product-count]') ||
						doc.querySelector('#productCount') ||
						doc.querySelector('.collection-product-grid__header-count');
					if (newCount) {
						this.productCountContainer.innerHTML = newCount.innerHTML;
					}
				}

				if (this.activeFiltersContainer) {
					const newActiveFilters =
						doc.querySelector('#activeFilters') ||
						doc.querySelector('.collection-product-grid__active-filters');
					if (newActiveFilters) {
						this.activeFiltersContainer.innerHTML = newActiveFilters.innerHTML;
						this.activeFiltersContainer.setAttribute(
							'data-has-active-filters',
							newActiveFilters.getAttribute('data-has-active-filters') ||
								'false'
						);
					}
				}

				if (this.filtersContainer) {
					const newFilters =
						doc.querySelector('[data-filters-container]') ||
						doc.querySelector('.collection-product-grid__sidebar');
					if (newFilters) {
						this.filtersContainer.innerHTML = newFilters.innerHTML;

						const newCollectionFilters =
							this.filtersContainer.querySelector('collection-filters');
						if (newCollectionFilters) {
							newCollectionFilters.connectedCallback?.();
						}
					}
				} else {
					const newFiltersComponent = doc.querySelector('collection-filters');
					if (newFiltersComponent) {
						const openStates = {};
						this.querySelectorAll('details').forEach((details, index) => {
							openStates[index] = details.open;
						});

						this.innerHTML = newFiltersComponent.innerHTML;

						this.querySelectorAll('details').forEach((details, index) => {
							if (openStates[index] !== undefined) {
								details.open = openStates[index];
							}
						});

						this.bindEvents();
						this.initPriceRangeSliders();
					}
				}

				if (this.paginationContainer) {
					const newPagination =
						doc.querySelector('[data-pagination-container]') ||
						doc.querySelector('.pagination');
					if (newPagination) {
						this.paginationContainer.innerHTML = newPagination.innerHTML;
						this.bindPaginationEvents();
					}
				}

				if (this.productsContainer) {
					const offset = 100;
					const top =
						this.productsContainer.getBoundingClientRect().top +
						window.scrollY -
						offset;
					window.scrollTo({ top, behavior: 'smooth' });
				}
			}

			animateProducts() {
				if (!this.productsContainer) return;

				const products = this.productsContainer.querySelectorAll(
					'.product-card, .grid__item, [data-product-card]'
				);
				products.forEach((product, index) => {
					product.style.opacity = '0';
					product.style.transform = 'translateY(20px)';
					product.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

					setTimeout(() => {
						product.style.opacity = '1';
						product.style.transform = 'translateY(0)';
					}, index * 50);
				});
			}

			bindPaginationEvents() {
				if (!this.paginationContainer) return;

				this.paginationContainer.querySelectorAll('a[href]').forEach((link) => {
					link.addEventListener('click', (event) => {
						event.preventDefault();
						const url = new URL(link.href);
						this.fetchAndRender(url.search);
					});
				});
			}

			showLoading() {
				if (this.loadingOverlay) {
					this.loadingOverlay.hidden = false;
				}

				if (this.productsContainer) {
					this.productsContainer.style.opacity = '0.5';
					this.productsContainer.style.pointerEvents = 'none';
				}

				this.dispatchEvent(
					new CustomEvent('collection:loading', {
						bubbles: true
					})
				);
			}

			hideLoading() {
				if (this.loadingOverlay) {
					this.loadingOverlay.hidden = true;
				}

				if (this.productsContainer) {
					this.productsContainer.style.opacity = '';
					this.productsContainer.style.pointerEvents = '';
				}

				this.dispatchEvent(
					new CustomEvent('collection:loaded', {
						bubbles: true
					})
				);
			}

			showError() {
				console.warn('Failed to load filtered results. Please try again.');
			}

			debounce(fn, delay) {
				let timeoutId;
				return (...args) => {
					clearTimeout(timeoutId);
					timeoutId = setTimeout(() => fn.apply(this, args), delay);
				};
			}
		}
	);
}

document.addEventListener('click', (event) => {
	const paginationLink = event.target.closest(
		'[data-pagination-container] a[href]'
	);
	if (!paginationLink) return;

	const filtersComponent = document.querySelector('collection-filters');
	if (!filtersComponent) return;

	event.preventDefault();
	const url = new URL(paginationLink.href);
	filtersComponent.fetchAndRender?.(url.search);
});
